import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  getRetentionConfig,
  updateRetentionConfig,
  triggerRetentionPurge,
} from './maintenance.api';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { Stack } from '../../../components/layout/Stack';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog';
import { emitToast } from '../../../components/ui/Toast';

export function RetentionManagerCard() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'retention-config'],
    queryFn: getRetentionConfig,
  });

  const [notifications, setNotifications] = useState<number>(7);
  const [auditLog, setAuditLog] = useState<number>(2555);
  const [customerDocuments, setCustomerDocuments] = useState<number>(3650);
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);

  useEffect(() => {
    const config = query.data?.data;
    if (config) {
      if (typeof config.notifications === 'number') setNotifications(config.notifications);
      if (typeof config.auditLog === 'number') setAuditLog(config.auditLog);
      if (typeof config.customerDocuments === 'number') setCustomerDocuments(config.customerDocuments);
    }
  }, [query.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateRetentionConfig({
        notifications,
        auditLog,
        customerDocuments,
      }),
    onSuccess: () => {
      emitToast({
        type: 'success',
        title: 'Retention Policy Saved',
        description: 'Company data retention configuration updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'retention-config'] });
    },
    onError: (err: Error) => {
      emitToast({
        type: 'error',
        title: 'Save Failed',
        description: err.message || 'Could not update retention configuration.',
      });
    },
  });

  const purgeMutation = useMutation({
    mutationFn: triggerRetentionPurge,
    onSuccess: (res) => {
      setPurgeConfirmOpen(false);
      const purgedCount = res?.data?.purgedCount ?? 0;
      emitToast({
        type: 'success',
        title: 'Purge Completed',
        description: `Permanently removed ${purgedCount} expired records.`,
      });
    },
    onError: (err: Error) => {
      emitToast({
        type: 'error',
        title: 'Purge Failed',
        description: err.message || 'Failed to trigger data retention purge.',
      });
    },
  });

  const effectiveConfig = query.data?.data;
  const hasChanges =
    effectiveConfig &&
    (notifications !== effectiveConfig.notifications ||
      auditLog !== effectiveConfig.auditLog ||
      customerDocuments !== effectiveConfig.customerDocuments);

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Data Retention &amp; Archival (G-102)</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton rows={4} />
        </CardContent>
      </Card>
    );
  }

  const isCustom = effectiveConfig?.isCustom ?? false;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle>Data Retention &amp; Archival Policy</CardTitle>
              <StatusBadge
                status={isCustom ? 'info' : 'success'}
                label={isCustom ? 'Custom Override' : 'Statutory Default'}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPurgeConfirmOpen(true)}
              loading={purgeMutation.isPending}
            >
              <Trash2 className="w-4 h-4 mr-1 text-warning-500" />
              Purge Expired Records Now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Stack gap="5">
            <p className="text-xs text-text-secondary leading-relaxed">
              Configures data retention windows for compliance with Companies Act (§128), Chit Funds Act, and PMLA.
              Transient communications exceeding retention are purged by daily cron (02:00 UTC). Audit logs are append-only and protected against deletion.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-[6px] border border-border bg-surface-2 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-text-secondary" />
                  <span className="text-xs font-semibold text-text-primary">Transient Notifications</span>
                </div>
                <p className="text-[11px] text-text-muted mb-3">
                  Soft-deleted in-app notifications and gateway logs (default: 7 days).
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    value={notifications}
                    onChange={(e) => setNotifications(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 text-sm"
                  />
                  <span className="text-xs text-text-secondary">days</span>
                </div>
              </div>

              <div className="rounded-[6px] border border-border bg-surface-2 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-success-500" />
                  <span className="text-xs font-semibold text-text-primary">Audit Event Logs</span>
                </div>
                <p className="text-[11px] text-text-muted mb-3">
                  Statutory compliance hold (default: 2555 days / ~7 years). Immutable.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={365}
                    value={auditLog}
                    onChange={(e) => setAuditLog(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 text-sm"
                  />
                  <span className="text-xs text-text-secondary">days</span>
                </div>
              </div>

              <div className="rounded-[6px] border border-border bg-surface-2 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-text-secondary" />
                  <span className="text-xs font-semibold text-text-primary">Customer Documents</span>
                </div>
                <p className="text-[11px] text-text-muted mb-3">
                  Statutory KYC &amp; agreement preservation hold (default: 3650 days / 10 years).
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={365}
                    value={customerDocuments}
                    onChange={(e) => setCustomerDocuments(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 text-sm"
                  />
                  <span className="text-xs text-text-secondary">days</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!hasChanges}
              >
                Save Retention Settings
              </Button>
            </div>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={purgeConfirmOpen} onOpenChange={setPurgeConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning-500" />
              <DialogTitle>Confirm Data Retention Purge</DialogTitle>
            </div>
            <DialogDescription className="text-xs text-text-secondary pt-2">
              This will permanently delete soft-deleted notifications and transient logs that are older than your configured retention window ({notifications} days).
              Financial records and audit logs will remain intact and will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setPurgeConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={purgeMutation.isPending}
              onClick={() => purgeMutation.mutate()}
            >
              Confirm Purge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
